'use client';

import clsx from 'clsx';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import {
  Autoplay,
  EffectCoverflow,
  EffectFade,
  Navigation,
  Pagination,
  Keyboard,
} from 'swiper/modules';

import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import styles from './media-carousel.module.scss';
import { MediaCarouselProps } from './types';

export const MediaCarousel: FC<MediaCarouselProps> = ({
  isPagination,
  isNavigation,
  loop,
  autoplay,
  effect,
  slides,
  children,
}) => {
  const swiperRef = useRef<any>(null);
  const initialTheme = slides[0]?.dark ? 'dark' : 'light';
  const [theme, setTheme] = useState<'dark' | 'light'>(initialTheme);

  const onInit = (swiper: any) => {
    swiper.on('transitionStart', (slide: any) =>
      setTheme(slides[slide.realIndex]?.dark ? 'dark' : 'light')
    );
  };

  // ✅ Update focusable slide on change
  useEffect(() => {
    const swiper = swiperRef.current?.swiper;
    if (!swiper) return;

    const updateFocus = () => {
      swiper.slides.forEach((slide: HTMLElement, index: number) => {
        slide.setAttribute('tabindex', index === swiper.activeIndex ? '0' : '-1');
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', `Slide ${index + 1} of ${swiper.slides.length}`);
      });
    };

    swiper.on('slideChange', updateFocus);
    updateFocus();

    return () => {
      swiper.off('slideChange', updateFocus);
    };
  }, []);

  const pagination = () => {
    const bulletClass = clsx({
      [styles[`media-carousel__pagination__bullet`]]: true,
    });

    const bulletActiveClass = clsx({
      [styles[`media-carousel__pagination__bullet--active`]]: true,
    });

    return {
      enabled: isPagination,
      clickable: true,
      horizontalClass: clsx({
        [styles[`media-carousel__pagination`]]: true,
      }),
      bulletClass: `swiper-pagination-bullet ${bulletClass}`,
      bulletActiveClass: bulletActiveClass,
    };
  };

  return (
    <Swiper
      ref={swiperRef}
      modules={[EffectFade, EffectCoverflow, Autoplay, Navigation, Pagination, Keyboard]}
      autoHeight={true}
      pagination={pagination()}
      navigation={isNavigation}
      autoplay={autoplay ? { delay: 7000 } : false}
      loop={loop}
      effect={effect}
      keyboard={{ enabled: true, onlyInViewport: true }}
      fadeEffect={{ crossFade: effect === 'fade' }}
      onInit={onInit}
      role="region"
      aria-label="Media carousel"
      className={clsx({
        [styles[`media-carousel`]]: true,
        [styles[`media-carousel--${theme}`]]: theme,
      })}
    >
      {children &&
        children.map((slide: ReactNode, index: number) => (
          <SwiperSlide
            key={index}
            tabIndex={index === 0 ? 0 : -1} // Only the active slide is focusable
            aria-hidden={index !== 0} // Mark other slides as hidden from screen readers
          >
            <div>{slide}</div>
          </SwiperSlide>
        ))}
    </Swiper>
  );
};
